import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { useLazyGetMyDataExportQuery } from "api/account/accountApiSlice";
import { stationDayLabel } from "util/stationTime";

/**
 * DSGVO Art. 15/20 self-service data access (#1679 plan §8) — the other half
 * of the delete section. The account holder can take away what we hold about
 * them without writing to support.
 *
 * The request needs the session bearer, which a plain `<a href>` cannot
 * carry, so the JSON is fetched and turned into a download here.
 */
const DataExportSection: React.FC = () => {
  const { t } = useTranslation();
  const [fetchExport, { isFetching }] = useLazyGetMyDataExportQuery();
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setError(null);
    try {
      const data = await fetchExport().unwrap();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kolula-account-data-${stationDayLabel(data.exportedAt)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoking immediately can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setError(t("account.export.error"));
    }
  };

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-medium text-slate-700">
        {t("account.export.title")}
      </h2>
      <p className="text-sm text-slate-600">{t("account.export.description")}</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button
        size="lg"
        variant="outline"
        className="w-full"
        disabled={isFetching}
        onClick={handleDownload}
      >
        {isFetching ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("account.export.preparing")}
          </>
        ) : (
          <>
            <Download className="mr-2 h-5 w-5" />
            {t("account.export.button")}
          </>
        )}
      </Button>
    </div>
  );
};

export default DataExportSection;
